import { useEffect, useMemo, useRef, useState } from 'react';
import services from '../config/services.json';
import { tmdb } from '../lib/tmdb.js';
import PosterCard from '../components/PosterCard.jsx';
import MovieModal from '../components/MovieModal.jsx';

// The Vault — pre-2000 classics on your services, with decade chips.
// Phase 1 spec called for this; finally here.
const DECADES = [
  { id: 'all', label: 'All Pre-2000', gte: null,   lte: 1999 },
  { id: '90s', label: '90s',          gte: 1990, lte: 1999 },
  { id: '80s', label: '80s',          gte: 1980, lte: 1989 },
  { id: '70s', label: '70s',          gte: 1970, lte: 1979 },
  { id: '60s', label: '60s & earlier',gte: null, lte: 1969 },
];

export default function TheVault() {
  const [decade, setDecade] = useState('all');
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const sentinelRef = useRef(null);

  const dec = DECADES.find((d) => d.id === decade);

  const providerIds = useMemo(
    () => services.services.filter((s) => s.active).map((s) => s.tmdbProviderId).join('|'),
    []
  );

  const baseParams = useMemo(() => {
    const p = {
      providers: providerIds,
      sort: 'popularity.desc',
      media: 'movie',
      yearLte: dec.lte,
    };
    if (dec.gte != null) p.yearGte = dec.gte;
    return p;
  }, [providerIds, dec]);

  // Reset + load first 3 pages on decade change.
  useEffect(() => {
    let alive = true;
    setResults([]); setPage(0); setLoading(true);
    (async () => {
      try {
        const batch = await Promise.all(
          [1, 2, 3].map((i) => tmdb.discover({ ...baseParams, page: i }))
        );
        if (!alive) return;
        const seen = new Set();
        const merged = [];
        for (const r of batch) for (const m of r.results || []) {
          if (!seen.has(m.id)) { seen.add(m.id); merged.push(m); }
        }
        setResults(merged);
        setPage(3);
        setTotalPages(batch[0]?.total_pages || 0);
        setTotalResults(batch[0]?.total_results || 0);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [baseParams]);

  // Infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    if (page === 0 || page >= totalPages || loading) return;
    const o = new IntersectionObserver(async (entries) => {
      if (!entries[0].isIntersecting) return;
      o.disconnect();
      setLoading(true);
      try {
        const next = page + 1;
        const data = await tmdb.discover({ ...baseParams, page: next });
        setResults((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          return [...prev, ...(data.results || []).filter((m) => !seen.has(m.id))];
        });
        setPage(next);
      } finally { setLoading(false); }
    }, { rootMargin: '600px' });
    o.observe(sentinelRef.current);
    return () => o.disconnect();
  }, [page, totalPages, loading, baseParams]);

  return (
    <div className="hb-vault">
      <div className="hb-vault__header">
        <div>
          <div className="hb-vault__eyebrow">The Vault</div>
          <h1 className="hb-vault__title">Classics back row</h1>
          <p className="hb-vault__sub">
            Pre-2000 films available on your services.
            {totalResults > 0 && ` ${results.length.toLocaleString()} of ${totalResults.toLocaleString()} titles.`}
          </p>
        </div>
      </div>

      <div className="hb-vault__decades">
        {DECADES.map((d) => (
          <button
            key={d.id}
            type="button"
            className={'hb-chip' + (decade === d.id ? ' is-active' : '')}
            onClick={() => setDecade(d.id)}
          >{d.label}</button>
        ))}
      </div>

      <div className="hb-browse__grid">
        {loading && results.length === 0
          ? Array.from({ length: 18 }).map((_, i) => <div key={'sk' + i} className="hb-poster hb-poster--skeleton" />)
          : results.length === 0
            ? <div className="hb-empty">No classics found for this decade on your services.</div>
            : results.map((m) => (
                <PosterCard
                  key={`${m.media_type}:${m.id}`}
                  movie={m}
                  onClick={() => setOpenId({ tmdb_id: m.id, media_type: m.media_type || 'movie' })}
                />
              ))
        }
      </div>

      {page < totalPages && (
        <div ref={sentinelRef} className="hb-browse__sentinel">
          {loading ? 'Loading more classics…' : 'Scroll for more'}
        </div>
      )}
      {page >= totalPages && results.length > 0 && (
        <div className="hb-browse__end">That’s every title — {results.length.toLocaleString()} in total.</div>
      )}

      {openId && <MovieModal movieId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
